import { useTranslation } from "react-i18next";
import { appPaths, HOME_PATH } from "../../../shared/routing/appPaths";
import { usePaneQuery, useTabTitle, useWorkspace } from "../../../shell/workspace";
import AppButton from "../../../ui/buttons/AppButton";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import { runOnOwnConnector } from "../hosting/automaticHosting";
import { readPreparationAddress } from "./intentAddress";
import AuthorPreparation from "./author/AuthorPreparation";
import ForkPreparation from "./fork/ForkPreparation";
import ImportPreparation from "./import/ImportPreparation";
import ReleasePreparation from "./release/ReleasePreparation";
import MemoryPreparation from "./memory/MemoryPreparation";
import RunPreparation from "./run/RunPreparation";
import type { ThreadIntent } from "./threadIntent";

/**
 * `/threads/new?intent=…`: the Thread view in preparation (WA-11, F31). The work's setup sits
 * in the Thread pane, beside its context; opening it calls no model and creates nothing.
 * Start replaces this view with the new Thread in the same pane, and the new Thread, yours,
 * runs on your connector (F55); Close goes back.
 */
export default function ThreadPreparationPage() {
  const { t } = useTranslation();
  const [query] = usePaneQuery();
  const workspace = useWorkspace();
  const address = readPreparationAddress(query);
  // The tab names what is prepared, the item by its name where there is one (F116), not the section.
  useTabTitle(address === null ? null : {
    kind: "preparation",
    heading: t(`threads.prepare.titles.${address.intent.kind}`),
    name: "name" in address.intent ? address.intent.name : "",
  });

  if (address === null) {
    return (
      <div className="mx-auto max-w-3xl p-4">
        <StatusMessage tone="warning" title={t("threads.prepare.invalidTitle")}>
          <p>{t("threads.prepare.invalidBody")}</p>
        </StatusMessage>
      </div>
    );
  }
  const { intent, requestKey } = address;
  const opened = (threadId: string, connectorId?: string) => {
    // Not awaited: a Thread that can't run without your choice stays saved, and its hosting line asks.
    runOnOwnConnector(threadId, connectorId).catch(() => undefined);
    workspace.open(appPaths.thread(threadId));
  };
  const close = () => {
    // Opened beside its context, closing the tab shows the context alone; otherwise go back to it.
    if (intent.returnTo !== null) {
      workspace.close();
    } else {
      // `/threads` itself becomes a new Thread's draft (F108), so cancelling steps back instead.
      workspace.back(HOME_PATH);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-4">
      <header className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="app-title text-lg font-semibold">{t(`threads.prepare.titles.${intent.kind}`)}</h2>
        </div>
        {/* The one control that leaves a preparation cancels it (F87, W-11). */}
        <AppButton variant="secondary" size="sm" onClick={close}>{t("common.cancel")}</AppButton>
      </header>
      {/* A second preparation in this pane starts fresh: its state belongs to its request. */}
      <PreparationOf key={requestKey} intent={intent} requestKey={requestKey} onStarted={opened} />
    </div>
  );
}

function PreparationOf({ intent, requestKey, onStarted }: { intent: ThreadIntent; requestKey: string; onStarted: (threadId: string, connectorId?: string) => void }) {
  const { t } = useTranslation();
  switch (intent.kind) {
    case "run":
      return <RunPreparation intent={intent} requestKey={requestKey} onStarted={onStarted} />;
    case "import":
      return <ImportPreparation intent={intent} requestKey={requestKey} onStarted={onStarted} />;
    case "author":
    case "edit":
      return <AuthorPreparation intent={intent} requestKey={requestKey} onStarted={onStarted} />;
    case "fork":
      return <ForkPreparation intent={intent} requestKey={requestKey} onStarted={onStarted} />;
    case "release":
      return <ReleasePreparation intent={intent} requestKey={requestKey} onStarted={onStarted} />;
    case "memory":
      return <MemoryPreparation intent={intent} requestKey={requestKey} onStarted={onStarted} />;
    case "batch":
      // Its view comes with the package that moves its start here (plan §2).
      return (
        <StatusMessage tone="info" title={t("threads.prepare.notYetTitle")}>
          <p>{t("threads.prepare.notYetBody")}</p>
        </StatusMessage>
      );
  }
}
